import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { PersonalInfo } from '../types';

interface PersonalInfoSectionProps {
  personalInfo: PersonalInfo;
}

const PersonalInfoSection = ({ personalInfo }: PersonalInfoSectionProps) => {
  return (
    <section className="neubrutal-card bg-white p-8 sm:p-12 mb-12 relative overflow-hidden group">
      {/* Subtle Pattern Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px'
        }} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
        {/* Professional Photo */}
        <div className="lg:col-span-4 max-w-sm mx-auto w-full">
          <div className="relative">
            <div className="aspect-[4/5] rounded-[1.5rem] overflow-hidden border-2 border-[#0C2C39] shadow-[4px_4px_0px_0px_#0C2C39] transition-all duration-500 group-hover:shadow-none">
              <Image
                src={personalInfo.profileImage || '/image/me.jpg'}
                alt={personalInfo.name}
                className="w-full h-full object-cover grayscale brightness-90 contrast-110 hover:grayscale-0 transition-all duration-700"
              />
            </div>

            <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-[#FFF9EB] border-2 border-[#0C2C39] rounded-xl flex items-center justify-center text-[#0C2C39] shadow-[2px_2px_0px_0px_#0C2C39] transform rotate-3">
              <Icon name="User" size={24} />
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="lg:col-span-8 flex flex-col justify-center">
          <div className="mb-8">
            <div className="inline-block px-4 py-1.5 rounded-full bg-[#0C2C39]/5 text-[#4A5568]/60 text-[10px] font-black uppercase tracking-[0.2em] mb-4">
              Professional Directory / 01
            </div>
            <h1 className="text-5xl sm:text-7xl font-black text-[#0C2C39] mb-4 tracking-tighter leading-[1] font-roboto">
              {personalInfo.name}
            </h1>
            <p className="text-xl text-teal-700/80 font-black uppercase tracking-widest">{personalInfo.title}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-12 mb-10 pb-10 border-b-2 border-[#0C2C39]/5">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl bg-[#0C2C39]/5 flex items-center justify-center text-[#0C2C39]">
                <Icon name="MapPin" size={18} />
              </div>
              <span className="text-[#0C2C39] font-black uppercase tracking-wider text-xs">{personalInfo.location}</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl bg-[#0C2C39]/5 flex items-center justify-center text-[#0C2C39]">
                <Icon name="Mail" size={18} />
              </div>
              <span className="text-[#0C2C39] font-black uppercase tracking-wider text-xs">{personalInfo.email}</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl bg-[#0C2C39]/5 flex items-center justify-center text-[#0C2C39]">
                <Icon name="Phone" size={18} />
              </div>
              <span className="text-[#0C2C39] font-black uppercase tracking-wider text-xs">{personalInfo.phone}</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl bg-[#0C2C39]/5 flex items-center justify-center text-[#0C2C39]">
                <Icon name="Calendar" size={18} />
              </div>
              <span className="text-[#0C2C39] font-black uppercase tracking-wider text-xs">{personalInfo.yearsOfExperience}+ Years Experience</span>
            </div>
          </div>

          <div className="max-w-none">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#0C2C39]/40 mb-4">Executive Brief</p>
            <div className="text-[#0C2C39]/70 text-lg leading-relaxed font-medium">
              {personalInfo.bio}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalInfoSection;